<script setup lang="ts">
import { debounce } from 'throttle-debounce'

/* Utils */
import { getAutocompleteService } from '@/services/autocomplete/autocompleteService'
import { serializeToRequest } from '@/utils/serializeToRequest'

/* Constants */
import {
  TIME_OF_DEBOUNCE,
  COUNT_OF_QUERY,
  BOUND_OF_SEARCH_AUTOCOMPLETE,
  StateOfAutocomplete,
} from '@/constants/components/autocomplete'
import AppAutocomplete from '@/components/App/AppAutocomplete.vue'

/* Stores */
import { useCityStore } from '@/store/city/city'
import { useDeliveryStore } from '@/store/delivery/delivery'
import { useToggleStore } from '@/store/toggle/toggle'
import { useMapSettingsStore } from '@/store/map-settings/map-settings'

/* Types */
import { DeliveryAddress, PlacesSearch } from '@/store/delivery/delivery.types'
import { SuggestedOfAddress } from '@/api/modules/geo/geocode.types'
import { Success } from '@/api/results'
import { AutocompleteService } from '@/types/mapServices'

/* Components */
import DeliverySearchButtonPosition from '@/components/Delivery/DeliverySearchButtonPosition.vue'
import IconCloseAutocomplete from '@/components/Icons/IconCloseAutocomplete.vue'

const cityStore = useCityStore()
const deliveryStore = useDeliveryStore()
const { newFetchingApiEnabled } = useToggleStore()
const mapSettingsStore = useMapSettingsStore()

const { $apiService } = useNuxtApp()

let autocompleteService = null as AutocompleteService

const autocompleteError = ref<string | string[]>('')
const autocompleteList = ref<Partial<SuggestedOfAddress>[]>([])
const autocompleteLoading = ref<Boolean>(false)

const request = ref<string | undefined>(undefined)

const selectedAddress = ref<SuggestedOfAddress | null>(null)

const deliveryAddress = computed(() => deliveryStore.deliveryAddress)

const settingsForAutocomplete = computed(
  () => mapSettingsStore.mapSettings.suggestion_settings,
)

const cityInfo = computed(() => {
  return {
    countryName: cityStore.cityInfo.country?.name || '',
    cityName: cityStore.cityName || '',
    codeRegion: String(cityStore.cityState?.code) || '',
    nameRegion: cityStore.cityState?.name || '',
  }
})

watch(
  deliveryAddress.value.address,
  (newDeliveryAddress: DeliveryAddress) => {
    if (newDeliveryAddress.place !== PlacesSearch.DEFAULT)
      eventsHandlerOfMap(newDeliveryAddress.address)
  },
  { deep: true },
)

// Обработчик адреса выбранного на карте
const eventsHandlerOfMap = (deliveryAddress: SuggestedOfAddress | null) => {
  if (!deliveryAddress) {
    selectedAddress.value = null
    autocompleteError.value = StateOfAutocomplete.NOT_FOUND

    return
  }

  // Добавляем в лист выбранный адрес от карты
  autocompleteList.value = [{ ...deliveryAddress }]

  // Фиксируем адрес в автокомплите
  selectedAddress.value = deliveryAddress

  autocompleteError.value = ''
}

// Задержка запроса списка подсказок
const debounceSearchSuggestion = debounce(
  TIME_OF_DEBOUNCE,
  (value: string | undefined) => {
    searchSuggestions(value)
  },
)

// Новый fetch для автокомплита
const fetchFromNewSuggestions = async (request: string) => {
  const result = await $apiService.geocode.getSuggestedSearchesOfAddress(
    request,
    BOUND_OF_SEARCH_AUTOCOMPLETE.HOUSE,
    cityInfo.value.countryName,
    cityInfo.value.codeRegion,
    cityInfo.value.cityName,
  )

  if (!(result instanceof Success)) {
    setAutocompleteError(result.message)

    return
  }

  // Если нет подсказок
  if (!result.data.length) {
    setAutocompleteError(StateOfAutocomplete.NOT_FOUND)

    return
  }

  autocompleteList.value = result.data
}

// TODO: После раскатки v4, удалить старый сервис поиска подсказок
const fetchOldSuggestions = async (request: string) => {
  try {
    if (!autocompleteService) {
      autocompleteError.value = StateOfAutocomplete.NOT_WORKING

      return
    }

    const preparedRequest = serializeToRequest(
      request,
      cityInfo.value.countryName,
      cityInfo.value.nameRegion,
      cityInfo.value.cityName,
    )

    autocompleteList.value = await autocompleteService.getSuggestions(
      preparedRequest,
    )

    autocompleteError.value = ''
  } catch (error) {
    autocompleteError.value = StateOfAutocomplete.NOT_DETECTED
  }
}

const searchSuggestions = (request: string | undefined) => {
  if (!request || (request && request.length < COUNT_OF_QUERY)) {
    autocompleteError.value = StateOfAutocomplete.EMPTY

    return
  }

  autocompleteLoading.value = true

  // В зависимости от тагла определяем вызов
  newFetchingApiEnabled
    ? fetchFromNewSuggestions(request)
    : fetchOldSuggestions(request)

  autocompleteLoading.value = false
}

const setAutocompleteError = (errorMessage: string) => {
  autocompleteError.value = errorMessage
}

// Метод выбора адреса из списка autocomplete
const handleSelectSuggestion = (
  selectedSuggestion: SuggestedOfAddress | null,
) => {
  request.value = selectedSuggestion?.value

  deliveryStore.setDeliverySearch(PlacesSearch.AUTOCOMPLETE, selectedSuggestion)
}

// Фиксируем ввод строки
const handleSearchInput = (value: string) => {
  if (!request || (request && request.length < COUNT_OF_QUERY)) {
    autocompleteError.value = StateOfAutocomplete.EMPTY

    return
  }

  request.value = value || selectedAddress.value?.value

  debounceSearchSuggestion(request.value)
}

// Сбрасываем значение поля
const onResetSearch = () => {
  request.value = ''
  selectedAddress.value = null

  deliveryStore.setDeliverySearch(
    PlacesSearch.DEFAULT,
    deliveryStore.deliveryAddress.address.address,
  )
}

onMounted(async () => {
  autocompleteService = await getAutocompleteService(
    settingsForAutocomplete.value,
  )
})
</script>

<template>
  <div class="search">
    <app-autocomplete
      v-model="selectedAddress"
      :search="request"
      class="search__input"
      :items="autocompleteList"
      :menu-props="{ 'allow-overflow': true }"
      :error-messages="autocompleteError"
      autocomplete="off"
      menu-icon=""
      no-filter
      contained
      hide-no-data
      placeholder="Введите свой адрес"
      single-line
      item-title="value"
      return-object
      :loading="autocompleteLoading"
      @update:model-value="handleSelectSuggestion"
      @update:search="handleSearchInput"
    >
      <template v-if="request || selectedAddress" #append-inner>
        <icon-close-autocomplete
          class="search__clearable"
          @click="onResetSearch"
        />
      </template>
    </app-autocomplete>

    <delivery-search-button-position
      @set-autocomplete-error="setAutocompleteError"
    />
  </div>
</template>
<style lang="scss" scoped>
.search {
  position: relative;
  display: flex;
  max-width: 771px;

  &__clearable {
    height: 20px;
    cursor: pointer;
  }

  &__input {
    max-width: 100%;
    min-height: 48px;

    :deep(input) {
      padding: 0 16px;
      font-size: 14px;
      font-weight: 600;
      letter-spacing: normal;
    }

    :deep(input::placeholder) {
      font-size: 14px;
      font-weight: 600;
      color: #2c2f39;
    }

    :deep(.v-progress-linear__indeterminate) {
      color: $primary !important;
      opacity: 0 !important;
    }

    :deep(.v-autocomplete__selection) {
      height: 100%;
    }

    :deep(.v-autocomplete__selection-text) {
      font-size: 14px;
      font-weight: 600;
      line-height: normal;
      letter-spacing: normal;
    }

    :deep(.v-field__input) {
      height: 48px;
      min-height: 48px;
    }

    :deep(.v-messages__message) {
      font-size: 12px;
      font-weight: 600;
      line-height: 16px;
    }

    :deep(.v-field) {
      max-height: 48px;
      background-color: $button-background;
      border: none;
      border-radius: 10px;
      box-shadow: none;
    }
  }
}

@include media('>=tablet') {
  .search {
    &__input {
      :deep(input) {
        font-size: 16px;
        letter-spacing: normal;
      }

      :deep(input::placeholder) {
        font-size: 16px !important;
        letter-spacing: normal;
      }

      :deep(.v-autocomplete__selection-text) {
        font-size: 16px;
        letter-spacing: normal;
      }
    }
  }
}
</style>
